import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

export default function LoginPage() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [mode, setMode] = useState(sp.get("mode") === "register" ? "register" : "login");
  const [form, setForm] = useState({ email: "", password: "", password2: "", first_name: "", last_name: "", date_of_birth: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && form.password !== form.password2) {
      return toast.error("Le due password non coincidono");
    }
    setBusy(true);
    try {
      const data = mode === "login"
        ? await login(form.email, form.password)
        : await register({
            email: form.email, password: form.password,
            first_name: form.first_name, last_name: form.last_name,
            date_of_birth: form.date_of_birth || null,
            phone: form.phone,
          });
      toast.success(mode === "login" ? "Bentornata!" : "Account creato");
      nav(data.role === "admin" ? "/trainer" : "/app", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const forgot = async () => {
    if (!forgotEmail) return toast.error("Inserisci email");
    try {
      const { api } = await import("@/lib/api");
      await api.post("/auth/forgot-password", { email: forgotEmail });
      toast.success("Richiesta inviata a Sara. Ti ricontatterà con la nuova password.");
      setForgotOpen(false); setForgotEmail("");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative border-r border-white/10 overflow-hidden">
        <img
          src="https://images.pexels.com/photos/4753888/pexels-photo-4753888.jpeg"
          alt="Training"
          className="absolute inset-0 w-full h-full object-cover opacity-40 grayscale contrast-125"
        />
        <div className="relative z-10 h-full p-12 flex flex-col justify-end">
          <div>
            <img
              src="/logo.png"
              alt="Sara Absi · Chinesiologa Clinica & Personal Trainer"
              data-testid="brand-logo"
              className="h-14 lg:h-16 w-auto object-contain bg-white rounded-sm p-2 mb-6"
            />
            <h2 className="font-heading font-black uppercase tracking-tighter text-4xl lg:text-5xl leading-[0.95]">
              Il tuo percorso.<br />
              <span className="text-[#A855F7]">Il nostro metodo.</span>
            </h2>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-md fade-up">
          <div className="label-caps mb-3">
            {mode === "login" ? "Accedi al tuo account" : "Registrati come cliente"}
          </div>
          <h1 className="font-heading font-black uppercase tracking-tighter text-4xl mb-10">
            {mode === "login" ? "Bentornato" : "Benvenuto"}
          </h1>

          <form onSubmit={submit} className="space-y-6">
            {mode === "register" && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Nome" testid="input-first-name">
                    <input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="fld" />
                  </Field>
                  <Field label="Cognome" testid="input-last-name">
                    <input required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="fld" />
                  </Field>
                </div>
                <Field label="Data di nascita" testid="input-dob">
                  <input required type="date" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} className="fld" />
                </Field>
                <Field label="Numero di telefono" testid="input-phone">
                  <input required type="tel" pattern="[0-9+\-\s().]{6,25}" placeholder="Es. +39 334 3872824" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="fld" />
                </Field>
              </>
            )}
            <Field label="Email" testid="input-email">
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="fld" />
            </Field>
            <Field label="Password" testid="input-password">
              <input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="fld" />
            </Field>
            {mode === "register" && (
              <Field label="Conferma password" testid="input-password2">
                <input required type="password" value={form.password2} onChange={(e) => setForm({ ...form, password2: e.target.value })} className="fld" />
              </Field>
            )}

            <button
              data-testid="submit-btn"
              disabled={busy}
              type="submit"
              className="btn-primary w-full flex items-center justify-center gap-3"
            >
              {mode === "login" ? "Entra" : "Crea account"} <ArrowRight size={16} />
            </button>

            {mode === "login" && (
              <button type="button" onClick={() => setForgotOpen(true)} data-testid="forgot-password-btn"
                className="text-xs text-[#A855F7] hover:underline block mx-auto">
                Password dimenticata?
              </button>
            )}
          </form>

          <div className="mt-8 text-white/50 text-sm">
            {mode === "login" ? (
              <>
                Non hai un account?{" "}
                <button data-testid="switch-to-register" onClick={() => setMode("register")} className="text-[#A855F7] hover:underline">
                  Registrati
                </button>
              </>
            ) : (
              <>
                Hai già un account?{" "}
                <button data-testid="switch-to-login" onClick={() => setMode("login")} className="text-[#A855F7] hover:underline">
                  Accedi
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .fld {
          width: 100%; background: transparent;
          border: 0; border-bottom: 1px solid rgba(255,255,255,0.2);
          padding: 12px 0; color: #FFFFFF; outline: none;
          transition: border-color 150ms ease-out;
          font-family: 'Manrope', sans-serif; font-size: 1rem;
        }
        .fld:focus { border-bottom-color: #A855F7; }
      `}</style>

      {forgotOpen && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setForgotOpen(false)}>
          <div onClick={e => e.stopPropagation()} className="bg-[#120B19] border border-white/10 p-6 w-full max-w-md">
            <div className="font-heading uppercase tracking-tight text-lg mb-4">Password dimenticata</div>
            <p className="text-sm text-white/60 mb-4">
              Inserisci la tua email. Sara riceverà una notifica e ti fornirà una nuova password.
            </p>
            <input type="email" required value={forgotEmail} onChange={e => setForgotEmail(e.target.value)}
              placeholder="tua@email.it" data-testid="forgot-email"
              className="w-full bg-transparent border-b border-white/20 p-2 focus:border-[#A855F7] outline-none mb-4 text-white" />
            <div className="flex gap-2">
              <button onClick={() => setForgotOpen(false)} className="btn-ghost flex-1">Annulla</button>
              <button onClick={forgot} data-testid="forgot-submit" className="btn-primary flex-1">Invia richiesta</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="label-caps block mb-2">{label}</span>
      {children}
    </label>
  );
}
